import { Box } from '@mui/material'
import React, { useEffect, useRef } from 'react'

type Props = {
  original: string
  value: string // stored HTML ('' = no edit yet, start from the original)
  onChange: (html: string) => void
  fontSize: number
}

// Only the formatting markup the toolbar can produce is kept.
const ALLOWED_TAGS = new Set([
  'B', 'STRONG', 'I', 'EM', 'U', 'SPAN', 'P', 'DIV', 'BR', 'FONT'
])
const ALLOWED_STYLE = /^(color|font-weight|font-style|text-decoration)$/i
const COLOR_RE = /^#[0-9a-f]{3,8}$|^[a-z]+$/i

// DOM-based whitelist sanitizer: parse, then rebuild ONLY allowed elements and
// safe style props via createElement — scripts, event handlers, img, etc. dropped.
function sanitizeHtml (html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const out = document.createElement('div')

  const walk = (src: Node, dest: Node) => {
    src.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        dest.appendChild(document.createTextNode(node.textContent ?? ''))
        return
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return
      const el = node as HTMLElement
      if (!ALLOWED_TAGS.has(el.tagName)) {
        walk(el, dest) // unwrap: keep children, drop the tag
        return
      }
      const clean = document.createElement(el.tagName.toLowerCase())
      for (let i = 0; i < el.style.length; i++) {
        const prop = el.style[i]
        if (ALLOWED_STYLE.test(prop)) {
          clean.style.setProperty(prop, el.style.getPropertyValue(prop))
        }
      }
      if (el.tagName === 'FONT') {
        const color = el.getAttribute('color')
        if (color && COLOR_RE.test(color)) clean.setAttribute('color', color)
      }
      walk(el, clean)
      dest.appendChild(clean)
    })
  }

  walk(doc.body, out)
  return out.innerHTML
}

const escapeHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

const originalToHtml = (s: string): string => escapeHtml(s).replace(/\n/g, '<br>')

// Always-editable text. Formatting is applied from the shared toolbar in
// ReadingControls (document.execCommand acts on the focused element's selection).
// The edited HTML is saved on blur. Key this by file name so it re-initialises
// when the underlying voiceover changes.
export const EditableRichText: React.FC<Props> = ({
  original,
  value,
  onChange,
  fontSize
}) => {
  const ref = useRef<HTMLDivElement>(null)

  // Fill the editable area once on mount (remounts when the file name key changes).
  useEffect(() => {
    if (ref.current) {
      ref.current.innerHTML = value ? sanitizeHtml(value) : originalToHtml(original)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const save = () => {
    if (ref.current) onChange(sanitizeHtml(ref.current.innerHTML))
  }

  return (
    <Box
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      onBlur={save}
      sx={{
        fontSize: `${fontSize}px`,
        lineHeight: 1.7,
        px: 1,
        mx: -1,
        borderRadius: 1,
        outline: 'none',
        transition: 'background-color 120ms',
        cursor: 'text',
        '&:hover': { bgcolor: 'action.hover' },
        '&:focus': {
          bgcolor: 'action.hover',
          boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.divider}`
        }
      }}
    />
  )
}
